import { Maybe, Scalars, useTopCategoriesQuery } from '@/graphql/generated';

import {
  CITIES_DATA_FIELD,
  CITY,
  DISCIPLINE,
  DISCIPLINES_DATA_FIELD,
  DS_CONSUMER,
  EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPES_DATA_FIELD,
  ROLE,
  ROLES_DATA_FIELD,
  SKILL,
} from '../../constants';
import { LinkBoxItem } from '../../types';
import { generateCategoryListItems } from '../../utils/generate-category-list-items';
import { useSerpsMatch } from '../use-serps-match';

type RawLinkBoxLocationDataType =
  | Array<
      | {
          jobCount?: Maybe<Scalars['Int']>;
          seoName?: Maybe<Scalars['String']>;
          location?: { name?: Maybe<Scalars['String']> } | undefined | null;
        }
      | null
      | undefined
    >
  | null
  | undefined;

type RawLinkBoxDataType =
  | Array<
      | {
          name?: Maybe<Scalars['String']>;
          seoName?: Maybe<Scalars['String']>;
        }
      | null
      | undefined
    >
  | null
  | undefined;

function normalizeLocationData(collection: RawLinkBoxLocationDataType): LinkBoxItem[] {
  return (
    collection
      ?.map((location) => {
        if (location?.seoName && location.location?.name) {
          return {
            seoName: location.seoName,
            name: location.location.name,
            ...(location?.jobCount ? { count: location.jobCount } : {}),
          };
        }

        return null;
      })
      .filter((location): location is LinkBoxItem => location !== null) ?? []
  );
}

function normalizeRelatedData(collection: RawLinkBoxDataType): LinkBoxItem[] {
  return (
    collection
      ?.map((entry) => {
        if (entry?.seoName && entry.name) {
          return {
            seoName: entry.seoName,
            name: entry.name,
          };
        }

        return null;
      })
      .filter((location): location is LinkBoxItem => location !== null) ?? []
  );
}

export function useTopCategories() {
  const serpMatch = useSerpsMatch();
  const city = serpMatch?.categoryValue ?? '';
  const includeLocationsNearby = serpMatch?.category === CITY ?? false;
  const isJobRoleSerp = serpMatch?.category === ROLE && !serpMatch.subcategory;
  const isSkillSerp = serpMatch?.category === SKILL && !serpMatch.subcategory;

  const { data } = useTopCategoriesQuery({
    variables: {
      consumer: DS_CONSUMER,
      limit: 10,
      city,
      includeLocationsNearby,
      isJobRoleSerp,
      isSkillSerp,
      relatedTo: serpMatch?.categoryValue || '',
    },
    errorPolicy: 'all',
  });

  const cities = generateCategoryListItems({ data, dataField: CITIES_DATA_FIELD, categoryName: CITY });
  const disciplines = generateCategoryListItems({ data, dataField: DISCIPLINES_DATA_FIELD, categoryName: DISCIPLINE });
  const employmentTypes = generateCategoryListItems({
    data,
    dataField: EMPLOYMENT_TYPES_DATA_FIELD,
    categoryName: EMPLOYMENT_TYPE,
  });
  const roles = generateCategoryListItems({ data, dataField: ROLES_DATA_FIELD, categoryName: ROLE });
  const citiesNearby = normalizeLocationData(data?.jobsLinkBoxTopLocationsNearby);
  const relatedJobRolesByJobRole = normalizeRelatedData(data?.jobsLinkBoxJobRolesRelatedJobRoles);
  const relatedSkillsByJobRole = normalizeRelatedData(data?.jobsLinkBoxJobRolesRelatedSkills);
  const relatedJobRolesBySkill = normalizeRelatedData(data?.jobsLinkBoxSkillsRelatedJobRoles);
  const relatedJobRoleSalaryStatistics = data?.jobsLinkBoxJobRoleSalaryEstimate;
  const topLocationsBySkill = normalizeLocationData(data?.jobsLinkBoxSkillTopLocations);

  return {
    cities,
    disciplines,
    employmentTypes,
    roles,
    citiesNearby,
    relatedJobRolesByJobRole,
    relatedSkillsByJobRole,
    relatedJobRolesBySkill,
    relatedJobRoleSalaryStatistics,
    topLocationsBySkill,
  };
}
